import {endDate, startDate} from '../../src/utils/dates.ts';
import {div, span} from '../../src/utils/html.ts';
import type {
	ArticleCardLayoutRecord,
	DigitalEditionItemData,
	Item,
	ListItemData,
	NodeChunk,
	Page
} from '../../types/types.ts';
import {createElement} from '../lib/dom.ts';
import {LoadResourceError} from '../lib/errors.ts';
import type {LayoutParser} from '../lib/layout-parser.ts';
import type {Store} from '../lib/store.ts';
import {Article, type ArticleData, type RelatedArticle, type Tag} from '../models/article.ts';
import {createChunk, findImageBeforeNextHeading} from '../pages/reader/lib/markup.ts';
import {executeSingleItemSource} from '../services/content.ts';
import {ContentAdapter} from './content-adapter.ts';

const ACPIdRegex = /\d+-\d+-\d+/;

const folioCodeMap: Record<string, string> = {
	NYH: 'Nyheter',
	MEN: 'Meninger',
	SPO: 'Sport',
	FEA: 'Feature',
	KUL: 'Kultur',
	PER: 'Personalia',
	SISTE: 'Siste'
};

const authorPromises = new Map();

export class AmediaContentAdapter extends ContentAdapter {
	readonly locale = 'no-NO';
	readonly timezone = 'Europe/Oslo';
	singleItemSourceId = '68beb54bc68e3921f21ff645';
	createEditionBrief(
		_data: DigitalEditionItemData,
		_store: Store,
		_articleIdsInEditiond: string[],
		_editionTitle: string
	): Article {
		throw new Error('Method not implemented.');
	}

	getSectionForPrintItem(printPage: Page, item: Item, _parser: LayoutParser): string {
		const printPageSection = getSectionFromFolioCode(printPage.properties?.folioCode);
		const section = printPageSection || item.data.section || item.data.category;
		item.data.category = section;

		return section ?? '';

		function getSectionFromFolioCode(folioCode: string | number | boolean | undefined) {
			if (typeof folioCode !== 'string') {
				return '';
			}

			const mainPart = folioCode.split('_')[1];
			if (!mainPart) {
				return '';
			}

			return folioCodeMap[mainPart] || mainPart;
		}
	}

	getStoryForPrintItem(_printPage: Page, _item: Item, parser: LayoutParser): string {
		const story = parser.getStory().replaceAll(/- + /g, '-');
		const kicker = parser.getKicker()?.innerHTML;
		// console.log(kicker, story);
		return kicker || story;
	}

	getListQueryParams(publishDate: string, tag: string, articleId: string): Record<string, number | string> {
		const params: Record<string, string | number> = {
			'params[limit]': 100
		};
		if (tag) {
			params['params[tags]'] = tag;
			return params;
		}

		if (articleId) {
			params['params[id]'] = articleId;
			return params;
		}

		const date = new Date(publishDate);

		return {
			'params[limit]': 100,
			'params[startDate]': startDate(date, this.timezone),
			'params[endDate]': endDate(date, this.timezone)
		};
	}

	formatListItems(
		items: ListItemData[],
		groupBy: string,
		_date: string | null,
		store: Store,
		sourceId?: string,
		_timezone?: string
	): Article[] {
		const list: Article[] = [];
		const currentSourceId = sourceId || this.singleItemSourceId;
		items.forEach((data) => {
			if (!ACPIdRegex.test(data.id)) {
				return;
			}

			if (data.id !== '5-142-275612') {
				// return;
			}

			if (data.type === 'video' || data.model === 'teaser') {
				return;
			}

			const articleData = normalizeListResult(data as unknown as AcpArticleData, groupBy, currentSourceId);
			const article = new Article(articleData, store.getItemState(`${currentSourceId}:${articleData.id}`));

			if (!article) {
				return;
			}

			list.push(article);
		});

		return list;
	}

	async loadItem(
		article: Article,
		groupBy: string,
		sortOrder: number | undefined,
		store: Store,
		sourceId?: string
	): Promise<Article | null> {
		const currentSourceId = sourceId || this.singleItemSourceId;
		const articleData = await normalize(
			article.data.data as unknown as AcpArticleData,
			groupBy,
			sortOrder,
			currentSourceId
		);
		return new Article(articleData, store.getItemState(`${currentSourceId}:${article.id}`));
	}

	async loadSingleItem(articleId: string, sourceId: string | undefined, store: Store): Promise<Article> {
		const currentSourceId = sourceId || this.singleItemSourceId;
		const articleDataResponse = await executeSingleItemSource<AcpArticleData>(currentSourceId, articleId);
		if (articleDataResponse.status !== 200) {
			throw new LoadResourceError(
				`Could not load article ${articleId} from source ${currentSourceId}`,
				articleDataResponse
			);
		}

		const normalizedData = await normalize(articleDataResponse.body, '', undefined, currentSourceId);
		const storeKey = `${sourceId}:${articleId}`;
		return new Article(normalizedData, store.getItemState(storeKey));
	}

	async loadRelatedArticles(article: Article, sourceId?: string) {
		const data = article.data.data as AcpArticleData;
		const currentSourceId = sourceId || article.sourceId || this.singleItemSourceId;
		return getRelatedArticles(data._embedded?.relations || [], currentSourceId);
	}
}

// TODO: Read all data from list result
function normalizeListResult(data: AcpArticleData, groupBy: string, sourceId: string) {
	const date = new Date(data.published);
	const section = getSection(data);

	const dateString = date.toLocaleDateString('no-NB', {
		weekday: 'long',
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	});

	let groupByValue: string;
	if (groupBy === 'day') {
		groupByValue = dateString;
	} else {
		groupByValue = groupBy;
	}

	return {
		id: data.fields.id,
		sourceId,
		objectId: null,
		publishedAt: data.published,
		sort: Date.parse(data.published),
		groupBy: groupByValue,
		section,
		data,
		layout: {
			html: ''
		},
		size: 0,
		pretitle: '',
		title: data.title,
		intro: data.leadText,
		story: getStory(data, section),
		sourceUrl: `https://${data._links.publication.title}${data.fields.relativeUrl}`
	};
}

async function normalize(
	data: AcpArticleData,
	groupBy: string,
	sortOrder: number | undefined,
	sourceId: string
): Promise<ArticleData> {
	// console.log(data);
	const body = cleanBodyText(data.body);
	const section = getSection(data);
	const chunks = await createContentGroups();
	await populateAuthors(data.authors);

	return {
		id: data.fields.id,
		sourceId,
		objectId: null,
		contentGroups: chunks,
		publishedAt: data.published,
		sort: sortOrder,
		groupBy: groupBy === 'Nyhet' ? 'Nyheter' : groupBy,
		section,
		data,
		layout: {
			html: ''
		},
		byline: {
			html: renderBylines(),
			items: data.authors
		},
		cardLayouts: data.cardLayouts,
		tags: data.tags,
		pretitle: '',
		// pretitle: data.tags?.map(({displayName}) => displayName).join(' / '),
		title: data.title,
		intro: data.leadText,
		size: Math.round(body.length / 40),
		story: getStory(data, section),
		sourceUrl: `https://${data._links.publication.title}${data.fields.relativeUrl}`
	};

	function cleanBodyText(body: string) {
		if (!body) {
			return '';
		}

		return body
			.replaceAll(/<p>(<b>)?(<i>)?(<b>)?(LES OGSÅ|Fått med deg disse): ?(<\/b>)?(<\/i>)?(<\/b>)?<\/p>/gi, '')
			.replaceAll(/<ul>\s*<li><b><i>HAR DU FÅTT MED DEG DISSE\?<\/i><\/b><\/li>\s*<\/ul>/gi, '');
	}

	async function populateAuthors(authors: AuthorData[]) {
		await Promise.all(
			authors.map(async (authorData) => {
				if (!authorData.email || authorData.email.endsWith('@ntb.no')) {
					return;
				}
				const photoUrl = `https://${data._links.publication.title}/api/rarity/v1/component/embed/journalist/photo?email=${authorData.email}`;
				if (!authorPromises.has(photoUrl)) {
					authorPromises.set(photoUrl, fetch(new Request(photoUrl, {method: 'HEAD'})));
				}
				const response = await authorPromises.get(photoUrl);
				if (response.ok) {
					authorData.photoUrl = photoUrl;
				} else {
					authorData.photoUrl = '';
				}
			})
		);
	}

	async function createContentGroups(): Promise<NodeChunk[]> {
		let figuresIndex = 1;
		const images = getImageRelations();

		const firstPageChunk = getFirstPageChunk(images);
		const bodyChunks = await getBodyChunks();
		const slideShowChunks = getSlideShowChunks(images);

		return [firstPageChunk, ...bodyChunks, ...slideShowChunks];

		async function getBodyChunks() {
			if (!body) {
				return [];
			}

			const tempEl = document.createElement('div');
			tempEl.innerHTML = body;
			if (!tempEl.firstElementChild) {
				return [];
			}
			const elements = Array.from(tempEl.firstElementChild.children) as HTMLElement[];

			await processBodyElements(elements, images);

			const updatedElements = Array.from(tempEl.firstElementChild.children) as HTMLElement[];

			return assignElementsToChunks(updatedElements);

			async function processBodyElements(
				elements: HTMLElement[],
				images: {
					data: AcpImageRelation;
					markup: string;
				}[]
			) {
				const imageMap = new Map();
				images.forEach(({data, markup}) => {
					imageMap.set(data.fields.inlineId, markup);
				});

				const embeddedRelations = getEmbeddedRelations();

				return Promise.all(
					elements.map(async (el) => {
						if (el.tagName === 'H2') {
							if (findImageBeforeNextHeading(el)) {
								el.classList.add('page-break-before');
							}
						}

						if (el.innerText.toLowerCase().startsWith('les også')) {
							el.remove();
							return;
						}

						const amediaEmbedEl = el.querySelector('amedia-embed');
						if (!amediaEmbedEl) {
							return;
						}

						const inlineId = amediaEmbedEl.attributes.getNamedItem('inlineId')?.value;
						if (imageMap.has(inlineId)) {
							amediaEmbedEl.innerHTML = imageMap.get(inlineId);
							const figureEl = amediaEmbedEl.querySelector('.picture');
							if (!figureEl) {
								el.remove();
								return;
							}
							figureEl.classList.add('figure');
							if (
								el.nextElementSibling?.tagName === 'H2' ||
								el.nextElementSibling?.querySelector('amedia-embed')
							) {
								figureEl.classList.add('page-break-after');
							}
							amediaEmbedEl.querySelector('.picture')?.classList.add('figure');
							el.insertAdjacentElement('beforebegin', figureEl);
							imageMap.delete(inlineId);
							amediaEmbedEl.remove();
							if (el.innerText === '') {
								el.remove();
							}
						} else {
							const relation = embeddedRelations.get(inlineId);
							if (!relation) {
								el.remove();
								return;
							}
							switch (relation.model) {
								case 'scrollytelling': {
									await renderScrollyTell(relation, el);
									el.remove();
									break;
								}
								case 'embed': {
									await renderEmbed(relation, el);
									el.remove();
									break;
								}
								default: {
									// console.log('skip relation', inlineId, relation.model, relation);
									el.remove();
								}
							}
							// TODO: Handle scrollytelling
							// We only map images for now, remove other embeds
						}
					})
				);

				async function renderEmbed(relation: AcpMarkupRelation, _el: HTMLElement) {
					if (relation.fields.smartEmbedUrl.includes('enkel-biografi')) {
						// Poor rendering, and too many requests
						return;
						// Very simple check for valid url

						// if (!relation.fields.smartEmbedUrl.startsWith('http')) {
						// 	return;
						// }
						// const figureEl = document.createElement('div');
						// figureEl.classList.add('embed--enkel-biografi');
						// figureEl.innerHTML = `<iframe class="amedia-embed" src="${relation.fields.smartEmbedUrl}"></iframe>`;
						// el.insertAdjacentElement('beforebegin', figureEl);
					}
					// const relData = await executeSingleItemSource<AcpArticleData>(
					// 	singleItemSourceId,
					// 	relation.fields.id
					// );
					// console.log(relData);
					// const figureEl = document.createElement('div');
					// figureEl.classList.add('figure', 'page-break-before', 'page-break-after');
					// figureEl.innerHTML = `<iframe class="amedia-embed" src="${el.querySelector('a')?.href}"></iframe>`;
					// el.insertAdjacentElement('beforebegin', figureEl);
				}

				// TODO: http://amedia.localhost:5174/feed/5943c177fb5b424f61e1cf07-5fb547e59dc34a320fda1936/5-128-1112307/4?date=2025-08-11

				async function renderScrollyTell(relation: AcpScrollytellingRelation, el: HTMLElement) {
					// console.log('handle embed', relation.fields.inlineId, relation.model, relation);
					let nodeToInsertAfter = el;

					const relData = await executeSingleItemSource<AcpScrollytellingArticleData>(
						sourceId,
						relation.fields.id
					);
					if (relData.status !== 200) {
						return;
					}
					relData.body._embedded?.relations.forEach((scrollyRel) => {
						// console.log('> ---', scrollyRel);
						const figureEl = createElement(el.ownerDocument, 'div', [
							'figure',
							'scrollytell',
							'page-break-before',
							'page-break-after'
						]);
						if (scrollyRel.type === 'imageRelation') {
							figureEl.style.backgroundImage = `url(${scrollyRel.fields.versions.large.url})`;
							if (scrollyRel.fields.photographer) {
								const creditEl = createElement(el.ownerDocument, 'div', ['scrollytell__credit']);
								creditEl.innerHTML = scrollyRel.fields.photographer;
								figureEl.appendChild(creditEl);
							}
						}

						if (scrollyRel.fields['scrollytelling-text']) {
							JSON.parse(scrollyRel.fields['scrollytelling-text']).forEach((item: ScrollytellingItem) => {
								// console.log(item);
								const itemEl = createElement(el.ownerDocument, 'div', [
									'scrollytell__item',
									`scrollytell__item--${item.type}`
								]);

								itemEl.style.setProperty(
									'--background-color',
									`var(--palette-bg-${item.background}, ${item.background})`
								);

								const contentNodes = [];
								if (item.type === 'text' && item.subtitle) {
									contentNodes.push(
										`<div class="scrollytell__item__subtitle">${item.subtitle || ''}</div>`
									);
								}

								if (item.text) {
									contentNodes.push(
										`<div class="scrollytell__item__text">${item.text.join('\n')}</div>`
									);
								}

								if (item.type === 'quote' && item.source) {
									contentNodes.push(
										`<div class="scrollytell__item__source">${item.source || ''}</div>`
									);
								}

								itemEl.innerHTML = contentNodes.join('\n');
								figureEl.append(itemEl);
							});
						}

						if (figureEl.innerHTML || scrollyRel.type === 'imageRelation') {
							nodeToInsertAfter.insertAdjacentElement('afterend', figureEl);
							nodeToInsertAfter = figureEl;
						} else {
							// TODO: Handle video with no text, or other unknown content
						}
						// console.log('< ---');
					});
				}
			}
		}

		function getFirstPageChunk(
			images: {
				data: AcpImageRelation;
				markup: string;
			}[]
		) {
			const firstPageChunk = createChunk();

			const leadAssetMarkup = images
				.filter(({data}) => data.fields.relationGroup === 'top')
				.map(({markup}) => markup)
				.join('\n');

			const leadAssetTempEl = document.createElement('div');
			leadAssetTempEl.innerHTML = leadAssetMarkup;
			firstPageChunk.figures.push(
				...Array.from(leadAssetTempEl.children)
					.map((el) => {
						if (!el.firstElementChild) {
							return null;
						}
						el.firstElementChild.classList.add('figure', `figure-${figuresIndex++}`);
						return el.firstElementChild;
					})
					.filter((el) => el instanceof HTMLElement)
			);

			return firstPageChunk;
		}

		function getSlideShowChunks(
			images: {
				data: AcpImageRelation;
				markup: string;
			}[]
		) {
			const slideShowMarkup = images
				.filter(({data}) => data.fields.relationGroup === 'slideshow')
				.map(({markup}) => markup)
				.join('\n');

			const slideShowTempEl = document.createElement('div');
			slideShowTempEl.innerHTML = slideShowMarkup;

			const slideShowChunks: NodeChunk[] = [];
			Array.from(slideShowTempEl.children).forEach((el) => {
				if (!el.firstElementChild) {
					return;
				}
				el.firstElementChild.classList.add('figure', `figure-${figuresIndex++}`);
				slideShowChunks.push(createChunk([], [el.firstElementChild as HTMLElement]));
			});

			return slideShowChunks;
		}

		function assignElementsToChunks(updatedElements: HTMLElement[]) {
			const chunks: NodeChunk[] = [];

			let currentChunk = createChunk();
			chunks.push(currentChunk);
			updatedElements.forEach((el: HTMLElement) => {
				if (el.classList.contains('page-break-before')) {
					if (currentChunk.nodes.length) {
						currentChunk = createChunk([el]);
						chunks.push(currentChunk);
					} else {
						currentChunk.nodes.push(el);
					}

					if (el.classList.contains('page-break-after')) {
						currentChunk = createChunk();
						chunks.push(currentChunk);
					}

					return;
				}

				if (el.classList.contains('figure')) {
					// Large figure, and we already have a another large figure in this chunk
					if (currentChunk.figures.length === 1 && el.classList.contains('picture--large')) {
						currentChunk = createChunk();
						chunks.push(currentChunk);
					}
					el.classList.add(`figure-${figuresIndex++}`);
					currentChunk.figures.push(el);
				} else {
					currentChunk.nodes.push(el);
					currentChunk.chars = currentChunk.chars + el.innerText.length;
				}
			});

			return chunks;
		}
	}

	function renderBylines() {
		if (!data.authors) {
			return '';
		}
		return div(
			data.authors
				.map((author) => {
					const profileTitle = '';
					return `
<div class="byline--web">
	${author.photoUrl ? div(`<img src="${author.photoUrl}"/>`, 'byline__photo') : ''}
	<div class="byline__text">
		${div(author.name, 'byline__name')}
		${div(author.email, 'byline__email')}
		${div(profileTitle, 'byline__title')}
	</div>
</div>
`;
				})
				.join(''),
			`byline bylines--${data.authors.length}`
		);
	}

	function getImageRelations() {
		if (!data._embedded?.relations) {
			return [];
		}

		const usedInlineIds = new Set();
		const relations = data._embedded.relations.filter((relation) => {
			if (relation.type !== 'imageRelation' || relation.fields.relationGroup === 'extra') {
				return false;
			}
			if (usedInlineIds.has(relation.fields.inlineId)) {
				return false;
			}

			usedInlineIds.add(relation.fields.inlineId);

			return true;
		}) as AcpImageRelation[];

		return relations.map((imageRef) => {
			let captionHTML = '';

			if (imageRef.fields.caption || imageRef.fields.photographer) {
				captionHTML = div(
					`${span(imageRef.fields.caption, 'caption')} ${imageRef.fields.photographer ? span(`Foto: ${imageRef.fields.photographer}`, 'credit') : ''}`,
					'caption-wrapper'
				);
			}
			const {width, height} = imageRef.fields;

			const size = width >= 1024 ? 'large' : 'small';
			const format = getFormat(width, height);
			const render = renderMode(imageRef);

			return {
				data: imageRef,
				markup: `
<div data-inlineId="${imageRef.fields.inlineId}">
	<div class="picture picture--${format} picture--${size}" data-render="${render}">
			<div class="image-wrapper" style="--asset-aspect-ratio: ${imageRef.fields.width} / ${imageRef.fields.height}"><img src="${imageRef.fields.versions.large.url}"></div>
	        ${captionHTML}
    </div>
</div>
`
			};
		});
	}

	function getEmbeddedRelations() {
		return (data._embedded?.relations || []).reduce((acc, curr) => {
			acc.set(curr.fields.inlineId, curr);
			return acc;
		}, new Map());
	}

	function renderMode(imageRef: AcpImageRelation) {
		if (imageRef.fields.title?.startsWith('Terningvenstre')) {
			return 'inline';
		}

		const {width, height} = imageRef.fields;

		if (Math.min(width, height) < 400) {
			return 'inline';
		}

		return '';
	}

	function getFormat(width: number, height: number) {
		const imageHeightRatio = height / width;
		if (imageHeightRatio < 0.75) {
			return 'landscape';
		}

		if (imageHeightRatio < 0.9) {
			return '4_3';
		}
		if (imageHeightRatio < 1.1) {
			return 'square';
		}
		if (imageHeightRatio < 1.34) {
			return '3_4';
		}

		return 'portrait';
	}
}

async function getRelatedArticles(relations: AcpRelation[], sourceId: string): Promise<RelatedArticle[]> {
	const rels = relations.filter((rel) => {
		// TODO: Check whether relationGroup === 'extra' is more relevant
		return (
			rel.type === 'contentRelation' &&
			rel.fields.relationGroup === 'com.escenic.inlineRelations' &&
			rel.model === 'story'
		);
	}) as AcpContentRelation[];

	const relPromises = await Promise.all(
		rels.map(async (rel: AcpContentRelation) => {
			const relData = await executeSingleItemSource<AcpArticleData>(sourceId, rel.fields.id);
			if (relData.status !== 200) {
				return;
			}

			const frontPageAsset = relData.body._embedded?.relations?.find(
				(embedded) => embedded.fields.relationGroup === 'frontpageImage'
			) as AcpImageRelation | undefined;

			return {
				id: rel.fields.id,
				title: rel.title,
				intro: rel.fields.leadText,
				publishedAt: relData.body.published,
				assetUrl: frontPageAsset?.fields.versions.large.url
			};
		})
	);

	return relPromises.filter((rel) => rel) as RelatedArticle[];
}

function getStory(data: AcpArticleData, section: string) {
	if (data.story) {
		return data.story;
	}
	if (section === 'Debatt' && data.fields.bodyVignette) {
		if (data.fields.bodyVignette === 'Meninger') {
			return '';
		}
		return `Debatt / ${data.fields.bodyVignette}`;
	}

	if (!data.tags?.length) {
		return '';
	}

	if (data.tags[0].displayName === 'Nyheter' && data.tags[1]) {
		return data.tags[1].displayName;
	}

	return data.tags[0].displayName;
}

function getSection(data: AcpArticleData) {
	if (data.model === 'opinion') {
		return 'Debatt';
	}

	if (!data.tags) {
		return 'Nyheter';
	}

	if (data.tags.some(({displayName}) => displayName === 'Kultur')) {
		return 'Kultur';
	}

	if (data.tags.some(({displayName}) => ['Sport', 'Fotball'].includes(displayName))) {
		return 'Sport';
	}

	if (
		data.tags.some(({displayName}) =>
			['Krim', 'Ulykke', 'Trafikkulykke', 'Ulykker og hendelser', 'Politi', 'Innbrudd', 'Brann'].includes(
				displayName
			)
		)
	) {
		return 'Krim / Ulykke';
	}

	if (data.tags.some(({displayName}) => ['Nyheter', 'Nyhet'].includes(displayName))) {
		return 'Nyheter';
	}

	// console.log(data.tags.map(({displayName}) => displayName));
	// return data.tags?.[0]?.displayName;
	return 'Nyheter';
}

export type AcpArticleData = {
	_links: {
		publication: {
			title: string;
		};
	};
	id?: string;
	cardLayouts: {
		desktop: ArticleCardLayoutRecord;
		mobile: ArticleCardLayoutRecord;
	};
	model: 'video' | 'story' | 'opinion' | 'feature';
	title: string;
	story?: string;
	leadText: string;
	body: string;
	state: string;
	published: string;
	authors: AuthorData[];
	tags: Tag[];
	fields: {
		bodyVignette: string;
		relativeUrl: string;
		isPremium: boolean;
		id: string;
	};
	_embedded: {
		relations: Array<AcpRelation>;
	};
	metadata: {
		slug: string;
	};
};

type AcpRelation =
	| AcpImageRelation
	| AcpContentRelation
	| AcpMarkupRelation
	| AcpVideoRelation
	| AcpScrollytellingRelation;

type AuthorData = {
	name: string;
	email: string;
	photoUrl: string;
	fields: object;
};

type AcpScrollytellingArticleData = {
	model: 'scrollytelling';
	_embedded: {
		relations: Array<AcpScrollytellingVideoItem | AcpScrollytellingImageItem>;
	};
};

type AcpImageRelation = {
	type: 'imageRelation';
	inline: boolean;
	fields: {
		caption: string;
		relationGroup: 'top' | 'extra' | 'slideshow' | 'com.escenic.inlineRelations' | 'frontpageImage';
		title?: string;
		color: string;
		'scrollytelling-text': '[]';
		fullscreen: boolean;
		versions: {
			large: {
				url: string;
				width: number;
				height: number;
			};
		};
		width: number;
		height: number;
		photographer: string;
		inlineId: string;
		id: string;
		alignment: string;
	};
	model: 'picture';
};

type AcpContentRelation = {
	type: 'contentRelation';
	model: 'story' | 'factbox' | 'feature';
	title: string;
	inline: boolean;
	fields: {
		relationGroup: 'extra' | 'com.escenic.inlineRelations';
		color: string;
		facttype: string;
		isPremium: boolean;
		inlineId: string;
		lastModifiedDate: string;
		id: string;
		alignment: string;
		relativeUrl: string;
		screenshotUrl: string;
		title: string;
		leadText: string;
		height: 'collapsed';
	};
};

type AcpMarkupRelation = {
	type: 'markupRelation';
	model: 'embed';
	title: string;
	inline: boolean;
	fields: {
		relationGroup: string;
		inlineId: string;
		alignment: string;
		smartEmbedUrl: string;
	};
};

type AcpScrollytellingRelation = {
	type: 'contentRelation';
	model: 'scrollytelling';
	title: string;
	inline: boolean;
	fields: {
		id: string;
		inlineId: string;
		isPremium: boolean;
		lastModifiedDate: string;
		relationGroup: string;
		relativeUrl: string;
		screenshotLastModifiedDate: string;
		title: string;
	};
};

type AcpVideoRelation = {
	type: 'videoRelation';
	model: 'video';
	title: string;
	inline: boolean;
	fields: {
		relationGroup: string;
		view: string;
		nocom: string;
		color: string;
		lastModifiedDate: string;
		videoId: string;
		inlineId: string;
		id: string;
		title: string;
		alignment: string;
	};
};

type AcpScrollytellingVideoItem = {
	type: 'videoRelation';
	inline: boolean;
	fields: {
		relationGroup: 'slideshow';
		view: string;
		nocom: string;
		color: string;
		lastModifiedDate: string;
		videoId: string;
		inlineId: string;
		id: string;
		title: string;
		alignment: string;
		'scrollytelling-text': string;
	};
	model: 'video';
};

type AcpScrollytellingImageItem = {
	type: 'imageRelation';
	inline: boolean;
	fields: {
		relationGroup: 'slideshow';
		color: string;
		lastModifiedDate: string;
		obscuraBackend: 'ece5';
		title: string;
		relativeUrl: string;
		'scrollytelling-text': string;
		fullscreen: boolean;
		versions: {
			large: {
				url: string;
				width: number;
				height: number;
			};
		};
		width: number;
		photographer: string;
		inlineId: string;
		id: string;
		alignment: string;
		height: number;
	};
	model: 'video';
};

type ScrollytellingItem = ScrollytellingQuoteItem | ScrollytellingTextItem;

type ScrollytellingTextItem = {
	type: 'text';
	id: string;
	open: boolean;
	background: string;
	text: string[];
	subtitle: string;
};

type ScrollytellingQuoteItem = {
	type: 'quote';
	id: string;
	open: boolean;
	background: string;
	text: string[];
	source: string;
};
